import React, { useCallback, useEffect, useRef, useState } from 'react';
import { notesApi } from '@/lib/notesApi';
import {
  History, Plus, Flag, CheckCircle2, RotateCcw, ArrowRightLeft, Pencil,
  CalendarDays, BellRing, Repeat, Hash, Paperclip, FolderKanban,
} from 'lucide-react';

const STATUS_LABEL = { todo: 'To do', in_progress: 'In progress', done: 'Done' };

function relTime(iso) {
  if (!iso) return '';
  const d = new Date(iso);
  if (isNaN(d.getTime())) return '';
  const diff = Date.now() - d.getTime();
  const m = Math.floor(diff / 60000);
  if (m < 1) return 'just now';
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  const days = Math.floor(h / 24);
  if (days < 7) return `${days}d ago`;
  return d.toLocaleDateString(undefined, { day: 'numeric', month: 'short' });
}

function fmtDate(iso) {
  if (!iso) return '';
  const d = new Date(iso);
  return isNaN(d.getTime()) ? '' : d.toLocaleString(undefined, { day: 'numeric', month: 'short', hour: 'numeric', minute: '2-digit' });
}

// Map an activity entry to an icon + human-readable phrase.
function describe(e) {
  switch (e.action) {
    case 'created':
      return { Icon: Plus, text: 'created this task' };
    case 'status':
      if (e.to === 'done') return { Icon: CheckCircle2, text: 'marked as Done', accent: true };
      if (e.from === 'done') return { Icon: RotateCcw, text: `reopened → ${STATUS_LABEL[e.to] || e.to}` };
      return { Icon: ArrowRightLeft, text: `moved ${STATUS_LABEL[e.from] || e.from} → ${STATUS_LABEL[e.to] || e.to}` };
    case 'renamed':
      return { Icon: Pencil, text: `renamed to “${e.to}”` };
    case 'priority':
      return { Icon: Flag, text: `set priority to ${e.to}` };
    case 'due':
      return { Icon: CalendarDays, text: e.to ? `set due ${fmtDate(e.to)}` : 'cleared the due date' };
    case 'reminder':
      return { Icon: BellRing, text: e.to ? `set a reminder for ${fmtDate(e.to)}` : 'cleared the reminder' };
    case 'repeat':
      return { Icon: Repeat, text: e.to && e.to !== 'none' ? `set repeat to ${e.to}` : 'turned off repeat' };
    case 'tags':
      return { Icon: Hash, text: `updated tags${e.detail ? ` (${e.detail})` : ''}` };
    case 'moved':
      return { Icon: FolderKanban, text: `moved to ${e.to}` };
    case 'attachment':
      return { Icon: Paperclip, text: `added ${e.detail || 'an attachment'}` };
    case 'attachment_removed':
      return { Icon: Paperclip, text: `removed ${e.detail || 'an attachment'}` };
    default:
      return { Icon: History, text: e.action };
  }
}

// ---------------------------------------------------------------------------
// Task history timeline. Loads /activity and applies live `task_activity`
// events forwarded from the shell WebSocket via the `event` prop.
// ---------------------------------------------------------------------------
export default function TaskActivity({ taskId, event }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState(false);
  const seenRef = useRef(new Set());

  const load = useCallback(async () => {
    if (!taskId) return;
    setLoading(true);
    try {
      const { data } = await notesApi.get(`/notes/tasks/tasks/${taskId}/activity`);
      seenRef.current = new Set((data || []).map((x) => x.id));
      setItems(data || []);
    } catch { setItems([]); } finally { setLoading(false); }
  }, [taskId]);

  useEffect(() => { setExpanded(false); load(); }, [load]);

  useEffect(() => {
    if (!event || event.task_id !== taskId || !event.entry) return;
    if (seenRef.current.has(event.entry.id)) return;
    seenRef.current.add(event.entry.id);
    setItems((prev) => [event.entry, ...prev]);
  }, [event, taskId]);

  const shown = expanded ? items : items.slice(0, 5);

  return (
    <div data-testid="task-activity">
      <div className="mb-2 flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-[0.12em] text-[var(--n-muted-2)]">
        <History size={13} className="text-[var(--n-accent)]" /> History{items.length ? ` (${items.length})` : ''}
      </div>
      {loading ? (
        <div className="space-y-2">{[0, 1].map((i) => <div key={i} className="h-6 animate-pulse rounded-lg bg-[var(--n-canvas-2)]" />)}</div>
      ) : items.length === 0 ? (
        <p className="rounded-xl border border-dashed border-[var(--n-border)] px-3 py-3 text-center text-[12px] text-[var(--n-muted-2)]" data-testid="task-activity-empty">No history yet.</p>
      ) : (
        <>
          <ol className="relative ml-1 space-y-2.5 border-l border-[var(--n-line)] pl-4" data-testid="task-activity-list">
            {shown.map((e) => {
              const { Icon, text, accent } = describe(e);
              return (
                <li key={e.id} className="relative" data-testid="task-activity-item">
                  <span className={`absolute -left-[21px] top-0.5 grid h-4 w-4 place-items-center rounded-full border border-[var(--n-surface)] ${accent ? 'bg-[var(--n-accent)] text-white' : 'bg-[var(--n-canvas-2)] text-[var(--n-accent)]'}`}>
                    <Icon size={9} />
                  </span>
                  <div className="text-[12.5px] leading-snug text-[var(--n-ink-soft)]">
                    <span className="font-semibold text-[var(--n-ink)]">{e.actor_name}</span> {text}
                  </div>
                  <div className="text-[10.5px] text-[var(--n-muted-2)]" title={fmtDate(e.created_at)}>{relTime(e.created_at)}</div>
                </li>
              );
            })}
          </ol>
          {items.length > 5 && (
            <button onClick={() => setExpanded((v) => !v)} data-testid="task-activity-toggle"
              className="n-focus mt-2 text-[12px] font-medium text-[var(--n-accent)] hover:underline">
              {expanded ? 'Show less' : `Show all ${items.length}`}
            </button>
          )}
        </>
      )}
    </div>
  );
}
